<template>
  <v-container class="py-12">
    <v-row justify="center">
      <v-col
        xl="3"
        style="max-width: 350px;"
      >
        <div class="text-center mb-10">
          <img
            src="@/assets/img/logo.svg"
            width="160px"
            alt="Ultrax Logo"
          >
        </div>
        <v-slide-x-transition
          mode="out-in"
        >
          <div v-if="forgotPassword.show">
            <v-card
              color="white"
              class="pa-8 pt-5 elevation-10"
              rounded="lg"
            >
              <form @submit.prevent="submitForgotPassword()">
                <div class="text-h5 text-center mb-2">
                  {{ $t('reset_password.dialog.header') }}
                </div>

                <div class="caption grey--text text-center mb-6">
                  {{ $t('reset_password.description') }}
                </div>

                <validation-observer ref="forgotValidation">
                  <validation-provider
                    v-slot="{ errors }"
                    rules="required"
                  >
                    <v-text-field
                      v-model="forgotPassword.email"
                      :label="$t('text_input.login.username_email.title') "
                      :error-messages="errors"
                      autocomplete="email"
                    />
                  </validation-provider>
                </validation-observer>

                <v-btn
                  block
                  large
                  type="submit"
                  color="primary"
                  class="mt-4 text-none"
                  :loading="forgotPassword.submitting"
                >
                  {{ $t('button.submit') }}
                </v-btn>
                <v-btn
                  text
                  color="grey"
                  block
                  class="text-none mt-2"
                  small
                  @click="forgotPassword.show = false"
                >
                  <v-icon
                    left
                    x-small
                  >
                    fal fa-arrow-left
                  </v-icon>
                  {{ $t('button.back') }}
                </v-btn>
              </form>
            </v-card>
          </div>
          <v-card
            v-else
            color="white"
            class="pa-8 pt-5 elevation-10"
            rounded="lg"
          >
            <form @submit.prevent="submit()">
              <div class="text-h5 text-center mb-6">
                {{ $t("login.dialog.header") }}
              </div>
              <validation-observer ref="validation">
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="E-mail"
                >
                  <v-text-field
                    v-model="username"
                    :label="$t('text_input.login.username_email.title') "
                    :error-messages="errors"
                    autocomplete="username"
                  />
                </validation-provider>
                <validation-provider
                  v-slot="{ errors }"
                  rules="required"
                  name="Password"
                >
                  <password-field
                    v-model="password"
                    background-color="white"
                    :label="$t('text_input.login.password.title')"
                    :error-messages="errors"
                    class="mb-3"
                  />
                </validation-provider>
              </validation-observer>

              <div class="mb-4 text-center caption">
                <a
                  class="text-decoration-none"
                  @click.stop="forgotPassword.show = true"
                >{{ $t('button.forgot_password') }}</a>
              </div>

              <v-btn
                block
                large
                type="submit"
                color="primary"
                class="ma-0 text-none"
                :loading="submitting"
              >
                {{ $t('button.continue') }}
              </v-btn>
            </form>
          </v-card>
        </v-slide-x-transition>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PasswordField from '@/components/inputs/PasswordField.vue'
import { AuthApiService } from '@/data/services'
import ability from '@/plugins/ability'

export default {
    components: { PasswordField },
    data() {
      return {
        username: '',
        password: '',
        submitting: false,
        forgotPassword: {
          show: false,
          email: null,
          submitting: false
        }
      }
    },
    methods: {
      async submit() {
        if (!(await this.$refs.validation.validate())) {
          return
        }

        await this.login()
      },
      async login() {
        try {
          this.submitting = true

          try {
            await AuthApiService.discoverRegion(this.username)
          } catch (e) {

          }

          await AuthApiService.initCsrf()

          const response = await AuthApiService.login(this.username, this.password)

          ability.update(response.user.permission_rules)

          if (!this.$can('manage-organisation') && !this.$can('manage-team')) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.allowed_access.error'),
              color: 'error'
            })
            return
          }

          await this.$store.dispatch(this.$storeActions.AUTH_LOGIN, response.user)

          this.$router.push({ name: this.$routeNames.OVERVIEW })
        } catch (e) {
          if (e?.response?.data?.error === 'forbidden_player') {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.allowed_access.error'),
              color: 'error'
            })
            return
          } else if (e?.response?.data?.error === 'bad_login_credentials') {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.wrong_username_or_password.error'),
              color: 'error'
            })
            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.something_wrong.error'),
            color: 'error'
          })

          throw e
        } finally {
          this.submitting = false
        }
      },
      async submitForgotPassword() {
        if (!(await this.$refs.forgotValidation.validate())) {
          return
        }

        this.forgotPassword.submitting = true

        try {
          try {
            await AuthApiService.discoverRegion(this.forgotPassword.email)
          } catch (e) {

          }

          const result = await AuthApiService.forgotPassword({
            email: this.forgotPassword.email
          })

          if (!result.status) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.user_not_found.error'),
              color: 'error'
            })
          } else {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.reset_email.success'),
              color: 'success'
            })

            this.forgotPassword.show = false
          }
        } catch (e) {
          if (e?.response?.status === 422 && e?.response?.data?.errors?.email) {
            this.$store.dispatch(this.$storeActions.ALERT, {
              text: this.$t('alert.user_not_found.error'),
              color: 'error'
            })

            return
          }

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('alert.something_wrong.error'),
            color: 'error'
          })

          throw e
        } finally {
          this.forgotPassword.submitting = false
        }
      }
    }
  }
</script>
